// Discard-reason taxonomy for the Issue Analytics drill-down.
//
// The Hub offers a few canned reasons ("Not relevant", "False positive", "Won't fix",
// "Duplicate", "Already fixed") and free text; almost half of production discards carry
// no reason at all. Each reason is mapped to one category, and categories roll up into
// three signal groups so the page can say *why* an agent gets rejected:
//   precision – the agent was wrong (false positive, can't reproduce, working as intended)
//   value     – the finding was right but not worth acting on (not relevant, won't fix,
//               out of the customer's control)
//   noise     – duplicates, timing and internal triage
// Rules are ordered: the canned labels win over free-text heuristics, and narrower
// phrases come before broad ones. Extend RULES when a new phrasing shows up as "Other".

export const REASON_GROUPS = {
  precision: { id: "precision", label: "Precision", hint: "the agent was wrong" },
  value: { id: "value", label: "Value", hint: "right, but not worth acting on" },
  noise: { id: "noise", label: "Noise", hint: "duplicates, timing, internal triage" },
  unknown: { id: "unknown", label: "Unknown", hint: "no usable reason" },
};

export const REASON_CATEGORIES = [
  { id: "false-positive", label: "False positive", group: "precision", color: "#EF4444" },
  { id: "cant-reproduce", label: "Can't reproduce", group: "precision", color: "#F97316" },
  { id: "by-design", label: "Working as intended", group: "precision", color: "#EC4899" },
  { id: "not-relevant", label: "Not relevant", group: "value", color: "#F59E0B" },
  { id: "wont-fix", label: "Won't fix", group: "value", color: "#EAB308" },
  { id: "third-party", label: "Out of their control", group: "value", color: "#A16207" },
  { id: "duplicate", label: "Duplicate", group: "noise", color: "#6366F1" },
  { id: "already-fixed", label: "Already fixed", group: "noise", color: "#22C55E" },
  { id: "in-progress", label: "In progress", group: "noise", color: "#14B8A6" },
  { id: "internal-review", label: "Internal bulk review", group: "noise", color: "#94A3B8" },
  { id: "other", label: "Other", group: "unknown", color: "#CBD5E1" },
  { id: "none", label: "No reason given", group: "unknown", color: "#E5E7EB" },
];

export const REASON_BY_ID = Object.fromEntries(REASON_CATEGORIES.map((c) => [c.id, c]));

const EMPTY_REASONS = new Set(["", "null", "none", "n/a", "na", "-", "--", ".", "no reason", "no comment"]);

// Ordered [category id, pattern]. The first match wins.
const RULES = [
  ["duplicate", /\bduplicat|\bdupe\b|same (issue|finding) as|already (reported|flagged|listed)|\bredundant\b/],
  ["internal-review", /augmented review|via pipeline|discarded via|bulk (review|discard)|auto[- ]discard/],
  [
    "already-fixed",
    /already (been )?(fixed|resolved|corrected|addressed|updated|removed|done)|has been (fixed|resolved|corrected|addressed)|(was|is|got) (fixed|resolved|corrected)|we (have )?fixed|now (fixed|resolved|corrected)|fixed (it|this|now|already)|resolved (it|this|now)|no longer (an issue|the case|there|present|exists|happening|appears)/,
  ],
  ["false-positive", /false[- ]?positive|\bfp\b/],
  ["wont-fix", /won'?t[- ]fix|wont[- ]fix|will not fix|not (going to|gonna) fix|do(es)?n'?t need (to be )?fix|no fix (needed|required|planned)/],
  ["not-relevant", /\bnot relevant|\birrelevant|not applicable|\bn\/a\b|doesn'?t apply|does not apply|not important|unimportant|out of scope|not in scope/],
  [
    "cant-reproduce",
    /can(no|')?t (recreate|reproduce|replicate|find|see)|could ?n'?t (recreate|reproduce|replicate|find|see)|unable to (recreate|reproduce|replicate|find|see)|not able to (recreate|reproduce|replicate)|(do not|don'?t|didn'?t|did not) see (the|this|any|an) (issue|problem)|(is|was) not (present|there|visible)|no such (issue|problem)/,
  ],
  [
    "third-party",
    /third[- ]party|3rd[- ]party|controlled by|shopify|salesforce|magento|bigcommerce|widget limitation|platform limitation|(app|plugin|vendor|theme|template) limitation|out of our control|not (in|under|within) our control|beyond our control|(can(no|')?t|cannot|unable to) (edit|change|modify|control|update|remove) (this|that|the|these|customer)/,
  ],
  [
    "in-progress",
    /working on (a|it|this|the)|in progress|will be (fixed|updated|addressed|corrected)|being (fixed|addressed|updated)|\b(planned|scheduled|roadmap)\b|a\/b test|\bab test|(currently|we are) testing|(we|dev|team) (will|are going to) (fix|update|address)/,
  ],
  [
    "by-design",
    /as (expected|intended|designed)|by design|intentional|intended|on purpose|expected behavio|(we|it was a|this was an?) (decision|choice)|\bprefer(red|s|ence)?\b|\bchose\b|\bdecided\b/,
  ],
  [
    "false-positive",
    /(this|it|that) is (fine|ok|okay|normal|standard|correct|accurate|not (wrong|incorrect|confusing|an error|an issue|a problem|a bug))|working (fine|correctly|properly|accurately|as it should)|works (fine|correctly|properly|as it should)|no (problem|issue) (here|with this)|there is no (problem|issue)|nothing (is )?wrong|not (a|an) (real )?(issue|problem|bug|error)|ai (does ?n'?t|did ?n'?t|can ?n'?t|cannot|fail(ed|s)? to) (recogni[sz]e|see|understand|detect|read|find|identify|know)|misinterpret|misread|mis-?identif|(not|isn'?t|is not) (accurate|true|valid|real)|inaccurate|incorrectly (flagged|detected|identified|reported)|wrongly|(flagged|reported) (incorrectly|by mistake)|(agent|zenyt|ai|scan|finding) (is|was) (wrong|mistaken|confused)|screen ?shot (is )?not|not representative|(is|are) correct\b/,
  ],
  [
    "not-relevant",
    /we (do not|don'?t|no longer) (have|use|offer|sell|carry|support|need)|(no longer|not) (needed|necessary|required|available|applicable)|discontinued|hidden from|not our (focus|business|customer)|\bb2b\b|majority of our customers|doesn'?t matter|does not matter|not (a )?concern|marketing (decision|team)/,
  ],
  ["wont-fix", /low priority|not (a )?priority|minimal (gain|impact|benefit)|not worth|too (much|expensive|costly)|no plans? to|leave (it|as is)|keep (it|as is)|accept(ed|able)( risk)?/],
];

export function normalizeReason(reason) {
  return (reason || "")
    .toLowerCase()
    .replace(/[‘’ʼ`]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

// Category id for one free-text reason.
export function classifyDiscardReason(reason) {
  const text = normalizeReason(reason);
  if (EMPTY_REASONS.has(text.replace(/[.!]+$/, ""))) return "none";
  for (const [id, pattern] of RULES) {
    if (pattern.test(text)) return id;
  }
  return "other";
}

// Category counts for a list of discard records, most common first, zero counts
// dropped. Records may carry a precomputed `reason_category`.
export function reasonMix(discards) {
  const counts = {};
  let total = 0;
  for (const d of discards || []) {
    const id = d.reason_category || classifyDiscardReason(d.discard_reason);
    counts[id] = (counts[id] || 0) + 1;
    total += 1;
  }
  return REASON_CATEGORIES.filter((c) => counts[c.id])
    .map((c) => ({ ...c, count: counts[c.id], share: total ? counts[c.id] / total : 0 }))
    .sort((a, b) => b.count - a.count);
}

// Roll a category mix up into the signal groups (precision / value / noise / unknown).
export function groupMix(mix) {
  const total = (mix || []).reduce((s, c) => s + c.count, 0);
  return Object.values(REASON_GROUPS)
    .map((g) => {
      const count = (mix || []).filter((c) => c.group === g.id).reduce((s, c) => s + c.count, 0);
      return { ...g, count, share: total ? count / total : 0 };
    })
    .filter((g) => g.count > 0);
}
